import {
	CheckCircleOutlined,
	CopyOutlined,
	DownOutlined,
	SearchOutlined,
	VerticalAlignBottomOutlined,
} from "@ant-design/icons";
import {
	Button,
	Card,
	Col,
	Dropdown,
	Grid,
	Image,
	Input,
	message,
	notification,
	Row,
	Select,
	Space,
	Tooltip,
	Typography,
} from "antd";
import Table, { TableProps } from "antd/lib/table";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import CustomPagination from "../../../components/CustomPagination";
import FilterOffice from "../../../components/form/FilterOffice";
import NotSet from "../../../components/NotSet";
import { httpRequest } from "../../../helpers/api";
import { getErrorMessage, showErrorMessage } from "../../../helpers/errorHandler";
import useAuth from "../../../hooks/useAuth";
import useFetchList from "../../../hooks/useFetchList";
import { EListingStatus, ListingProperties, ListListingQuery } from "../../../types/listing.type";
import Filter from "../../../components/Filter";
import { BaseResponseProps } from "../../../types/config.type";
import SelectCityFilter from "../../../components/SelectCityFilter";
import { LangItem } from "../Detail";
import { ListingMaskingHistoryProperties, ListingMaskingProperties } from "../../../types/listingMaskingHistory.type";
import { generateQueryString } from "../../../helpers/generateQueryString";
import { copyToClipboard, getWebUrl } from "../../../helpers/text";
import DropdownDownloadPdf from "./DropdownDownloadPdf";
import axios from "axios";
import { confirmModal } from "../../../helpers/modal";

export const DEFAULT_STATUS = [
	EListingStatus.PUBLISH,
	EListingStatus.UNPUBLISH,
	EListingStatus.EXPIRED,
	EListingStatus.CLOSED,
].join(",");

const MaskingTable: React.FC = () => {
	const { employee, user, isAgent, isFOPlus } = useAuth();
	const { md } = Grid.useBreakpoint();
	const [selectedListings, setSelectedListings] = useState<ListingProperties[]>([]);
	const { isLoading, data, pagination, search, setSearch, query, setQuery, changePage, fetchList } = useFetchList<
		ListingProperties,
		ListListingQuery
	>({
		endpoint: "listings",
		initialQuery: {
			filterStatus: DEFAULT_STATUS,
			sort: "updatedAt:DESC",
			includeData:
				"user,employee,office,listingPropertyType,maskingCount,copyListingMaskingCount,pdfListingMaskingCount,listingMaskingCountByAgent",
			excludeUserId: user.userId,
		},
	});

	const _handleFilterOffice = (value: string) => {
		setQuery({
			...query,
			offset: 0,
			filterOfficeId: value,
		});
	};

	const [isDownloadLoading, setIsDownloadLoading] = useState<boolean>(false);
	const [isDownloadLoadingMultiple, setIsDownloadLoadingMultiple] = useState<boolean>(false);
	const [isLoadingMaskingListing, setIsLoadingMaskingListing] = useState<boolean>(false);
	const [isLoadingShare, setIsLoadingShare] = useState<boolean>(false);
	const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

	const [api, contextHolder] = notification.useNotification();

	const LANG_EN = (
		<LangItem>
			<Image
				src="/en-flag.png"
				alt="ENG logo"
				width={24}
				height={15}
				preview={false}
			/>
			<span>ENG</span>
		</LangItem>
	);
	const LANG_ID = (
		<LangItem>
			<Image
				src="/id-flag.png"
				alt="IND logo"
				width={24}
				height={15}
				preview={false}
			/>
			<span>IDN</span>
		</LangItem>
	);

	const LANG_ITEMS = [
		{
			label: LANG_ID,
			key: "id",
		},
		{
			label: LANG_EN,
			key: "en",
		},
	];

	const COLUMNS: TableProps<ListingProperties>["columns"] = [
		{
			title: "LISTING ID",
			key: "listingCode",
			dataIndex: "listingCode",
			width: 150,
			sorter: (a: any, b: any) => a?.listingCode.localeCompare(b?.listingCode),
			render: (listingCode: string, record: ListingProperties) => {
				return (
					<Space>
						{isAgent && +record.maskingCount > 0 && <CheckCircleOutlined style={{ color: "#22c55e" }} />}
						<Link to={"/masking/" + record.listingId}>
							{listingCode ? listingCode?.toUpperCase() : <NotSet />}
						</Link>
					</Space>
				);
			},
		},
		{
			title: "TITLE",
			key: "title",
			dataIndex: "title",
			width: 400,
			render(value: string) {
				return <div style={{ minWidth: 200 }}>{value ?? <NotSet />}</div>;
			},
		},

		{
			title: "AGENT NAME",
			dataIndex: ["user", "nickName"],
			key: "nickName",
			width: 200,
			render: (nickName: string) => <>{nickName ? nickName?.toUpperCase() : <NotSet />}</>,
		},
		{
			title: "OFFICE",
			dataIndex: ["office", "officeName"],
			key: "officeName",
			width: 150,
		},
		{
			title: "STATUS",
			dataIndex: "status",
			key: "status",
			width: 100,
		},
		{
			title: "SUMMARY",
			dataIndex: "summary",
			key: "summary",
			width: "20%",
			render: (_: any, record: ListingProperties) => (
				<Space
					direction="vertical"
					size={4}
				>
					<Typography.Text style={{ fontSize: 14 }}>
						Total Copy Link: {record.copyListingMaskingCount || 0}
					</Typography.Text>
					<Typography.Text style={{ fontSize: 14 }}>
						Total Download: {record.pdfListingMaskingCount || 0}
					</Typography.Text>
					<Typography.Text style={{ fontSize: 14 }}>
						Total Mask by Agent: {record.listingMaskingCountByAgent || 0}
					</Typography.Text>
				</Space>
			),
		},
		isAgent
			? {
					title: "ACTION",
					dataIndex: "action",
					key: "action",
					width: 150,
					render: (listingId: string, record: ListingProperties) => {
						return (
							<Row
								gutter={[8, 8]}
								wrap={false}
								style={{ alignItems: "center", width: 150 }}
							>
								{+record.maskingCount > 0 ? (
									<>
										<Col>
											<Tooltip title="Copy Link">
												<Button
													style={{
														padding: "6px 10px",
													}}
													type="primary"
													icon={<CopyOutlined />}
													onClick={() => doMaskingLink(record)}
												></Button>
											</Tooltip>
										</Col>
										<Col>
											<DropdownDownloadPdf
												listing={record}
												notif={api}
												fetchList={fetchList}
											/>
										</Col>
									</>
								) : (
									<Col>
										<Dropdown
											menu={{
												items: LANG_ITEMS,
												onClick: async ({ key }) => {
													await doAction(key, record);
												},
											}}
											disabled={+record.maskingCount > 0 ? true : false}
										>
											<Button
												style={{
													padding: "6px 10px",
												}}
												type="primary"
												block
												disabled={+record.maskingCount > 0 ? true : false}
											>
												Mask
											</Button>
										</Dropdown>
									</Col>
								)}
							</Row>
						);
					},
			  }
			: null,
	].filter(Boolean) as TableProps<ListingProperties>["columns"];

	const doMaskingLink = async (listing: ListingProperties) => {
		setIsLoadingMaskingListing(true);
		try {
			const dto = {
				employeeId: employee.employeeId,
				listingId: listing.listingId,
				type: "LINK",
			};

			const res = await httpRequest.post<BaseResponseProps<ListingMaskingHistoryProperties>>(
				"/listing-masking-histories",
				dto
			);

			const permalink = `${getWebUrl()}/shared/${res.data.payload.listingSharedCode}`;

			copyToClipboard(permalink);

			fetchList();
			api.success({
				message: undefined,
				description: "Link successfully copied to your clipboard. You can share it now!",
				style: {
					backgroundColor: "#DFF4E0",
					border: "1px solid #55C95A",
				},
			});
		} catch (error) {
			message.error(getErrorMessage(error));
		} finally {
			setIsLoadingMaskingListing(false);
		}
	};

	const doDownloadMultiplePDF = async (lang?: "en" | "id") => {
		setIsDownloadLoadingMultiple(true);
		try {
			let copyUrl = [];

			for (const listing of selectedListings) {
				const dtoMasking = {
					employeeId: employee.employeeId,
					listingId: listing.listingId,
				};

				await httpRequest.post<BaseResponseProps<ListingMaskingProperties>>("/listing-maskings", dtoMasking);

				const dto = {
					employeeId: employee.employeeId,
					listingId: listing.listingId,
					type: "PDF",
				};
				await httpRequest.post<BaseResponseProps<ListingMaskingHistoryProperties>>(
					"/listing-masking-histories",
					dto
				);

				const dtoLink = {
					employeeId: employee.employeeId,
					listingId: listing.listingId,
					type: "LINK",
				};
				const res = await httpRequest.post<BaseResponseProps<ListingMaskingHistoryProperties>>(
					"/listing-masking-histories",
					dtoLink
				);

				let urlMasking = `${getWebUrl()}/shared/${res.data.payload.listingSharedCode}`;

				const resPdf: any = await axios({
					url: `${process.env.REACT_APP_PDF_URL}/pdf/listing/${listing.listingId}${generateQueryString(
						query
					)}`,
					method: "GET",
					headers: {
						"Content-Type": "application/json",
					},
					responseType: "blob",
				});
				if (res && res.data) {
					const url = window.URL.createObjectURL(new Blob([resPdf.data]));
					const link = document.createElement("a");
					link.href = url;
					link.setAttribute("download", `${listing.title}-${listing.listingCode}.pdf`);
					document.body.appendChild(link);
					link.click();
					link.parentNode?.removeChild(link);
				}

				copyUrl.push(urlMasking);
			}

			notification.success({
				message: undefined,
				description:
					"Successfully multiple copy link & Masking PDF download has been successful! You can now check it on your device.",
				style: {
					backgroundColor: "#DFF4E0",
					border: "1px solid #55C95A",
				},
			});

			fetchList();
			setSelectedListings([]);
			setSelectedRowKeys([]);

			copyToClipboard(copyUrl.join(","));
		} catch (error) {
			message.error(getErrorMessage(error));
		} finally {
			setIsDownloadLoadingMultiple(false);
		}
	};

	const doAction = async (lang?: string, listing?: ListingProperties) => {
		if (!listing?.pdfOrientation) return message.error("Please wait until loading finished");
		setIsDownloadLoading(true);
		try {
			const dtoMasking = {
				employeeId: employee.employeeId,
				listingId: listing.listingId,
			};

			await httpRequest.post<BaseResponseProps<ListingMaskingProperties>>("/listing-maskings", dtoMasking);

			// Link
			const dtoLink = {
				employeeId: employee.employeeId,
				listingId: listing.listingId,
				type: "LINK",
			};

			const res = await httpRequest.post<BaseResponseProps<ListingMaskingHistoryProperties>>(
				"/listing-masking-histories",
				dtoLink
			);

			// Pdf
			const dtoPdf = {
				employeeId: employee.employeeId,
				listingId: listing.listingId,
				type: "PDF",
			};

			await httpRequest.post<BaseResponseProps<ListingMaskingHistoryProperties>>(
				"/listing-masking-histories",
				dtoPdf
			);

			const query = {
				lang,
				listingCode: listing.listingCode,
				employeeId: employee.employeeId,
			};

			const resPdf: any = await axios({
				url: `${process.env.REACT_APP_PDF_URL}/pdf/listing/${listing.listingId}${generateQueryString(query)}`,
				method: "GET",
				headers: {
					"Content-Type": "application/json",
				},
				responseType: "blob",
			});

			if (resPdf && resPdf.data) {
				const url = window.URL.createObjectURL(new Blob([resPdf.data]));
				const link = document.createElement("a");
				link.href = url;
				link.setAttribute("download", `${listing.title}-${listing.listingCode}.pdf`);
				document.body.appendChild(link);
				link.click();
				link.parentNode?.removeChild(link);

				notification.success({
					message: undefined,
					description:
						"Successfully copy link & Masking PDF download has been successful! You can now check it on your device.",
					style: {
						backgroundColor: "#DFF4E0",
						border: "1px solid #55C95A",
					},
				});
			}

			let urlMasking = `${getWebUrl()}/shared/${res.data.payload.listingSharedCode}`;

			const permalink = urlMasking;

			copyToClipboard(permalink ?? "");

			fetchList();
		} catch (error) {
			showErrorMessage(error, true);
		}
		setIsDownloadLoading(false);
	};

	const rowSelection = {
		selectedRowKeys: selectedRowKeys,
		onChange: (selectedRowKeys: React.Key[], selectedRows: ListingProperties[]) => {
			console.log(`selectedRowKeys: ${selectedRowKeys}`, "selectedRows: ", selectedRows);
			setSelectedListings(selectedRows);
			setSelectedRowKeys(selectedRowKeys);
		},
		getCheckboxProps: (record: ListingProperties) => ({
			disabled: +record.maskingCount !== 0 || !isAgent,
		}),
		renderCell(checked: boolean, record: ListingProperties, index: number, node: React.ReactNode) {
			let message = "";
			if (!isAgent) {
				message = "Only agent can do this action";
			} else if (+record.maskingCount !== 0) {
				message = "Already mask";
			}

			if (!checked || (message && message !== "")) {
				return <Tooltip title={message}>{node}</Tooltip>;
			} else {
				return node;
			}
		},
	};

	return (
		<React.Fragment>
			{contextHolder}
			<Space
				direction="vertical"
				size="large"
			>
				<Filter maxWidth={"100%"}>
					{md ? (
						<>
							<Col md={{ span: 4 }}>
								<Select
									virtual={true}
									onChange={(v) => {
										if (v === "all") {
											setQuery((prev) => ({
												...prev,
												filterStatus: DEFAULT_STATUS,
												offset: 0,
											}));
										} else {
											setQuery((prev) => ({
												...prev,
												filterStatus: v,
												offset: 0,
											}));
										}
									}}
									className="outer-shadow"
									placeholder="Status"
									defaultValue="all"
									options={[
										{
											label: "All Listing",
											value: "all",
										},
										{
											label: "Publish",
											value: EListingStatus.PUBLISH,
										},
										{
											label: "Unpublish",
											value: EListingStatus.UNPUBLISH,
										},
										{
											label: "Expired",
											value: EListingStatus.EXPIRED,
										},
										{
											label: "Closed",
											value: EListingStatus.CLOSED,
										},
									]}
								/>
							</Col>
							<Col
								md={{ span: 12 }}
								lg={{ span: 12 }}
								xl={{ span: isFOPlus ? 12 : 9 }}
							>
								<Input
									className="outer-shadow"
									placeholder="Search by Title, Listing ID or Agent Name..."
									suffix={<SearchOutlined />}
									allowClear
									onChange={(e) => setSearch(e.target.value)}
									value={search}
								/>
							</Col>
						</>
					) : (
						<>
							<Col
								xs={{ span: 24 }}
								md={{ span: 12 }}
								lg={{ span: 12 }}
								xl={{ span: isFOPlus ? 12 : 9 }}
							>
								<Input
									className="outer-shadow"
									placeholder="Search by Title, Listing ID or Agent Name..."
									suffix={<SearchOutlined />}
									allowClear
									onChange={(e) => setSearch(e.target.value)}
									value={search}
								/>
							</Col>

							<Col
								xs={{ span: 12 }}
								md={{ span: 4 }}
							>
								<Select
									virtual={true}
									onChange={(v) => {
										if (v === "all") {
											setQuery((prev) => ({
												...prev,
												filterStatus: DEFAULT_STATUS,
												offset: 0,
											}));
										} else {
											setQuery((prev) => ({
												...prev,
												filterStatus: v,
												offset: 0,
											}));
										}
									}}
									className="outer-shadow"
									placeholder="Status"
									defaultValue="all"
									options={[
										{
											label: "All Listing",
											value: "all",
										},
										{
											label: "Publish",
											value: EListingStatus.PUBLISH,
										},
										{
											label: "Unpublish",
											value: EListingStatus.UNPUBLISH,
										},
										{
											label: "Expired",
											value: EListingStatus.EXPIRED,
										},
										{
											label: "Closed",
											value: EListingStatus.CLOSED,
										},
									]}
								/>
							</Col>
						</>
					)}

					<Col
						xs={{ span: 12 }}
						md={{ span: 4 }}
					>
						<SelectCityFilter
							placeholder="Search City"
							allowClear
							getPopupContainer={(triggerNode: HTMLElement) =>
								(triggerNode?.parentNode as HTMLElement) || document.body
							}
							onChange={(v) =>
								setQuery((prev) => ({
									...prev,
									filterCity: v,
									offset: 0,
								}))
							}
						/>
					</Col>

					{isFOPlus && (
						<Col
							xs={{ span: 12 }}
							md={{ span: 4 }}
						>
							<FilterOffice
								value={query.filterOfficeId}
								onChange={_handleFilterOffice}
							/>
						</Col>
					)}

					{isAgent && (
						<Col
							xs={{ span: 24 }}
							md={{ span: 6 }}
							lg={6}
							xl={7}
						>
							<Dropdown
								menu={{
									items: LANG_ITEMS,
									onClick: async ({ key }) => {
										if (selectedListings.length < 1) {
											return message.error("Please select a listing first before multiple mask!");
										} else {
											confirmModal({
												title: "Confirmation",
												subtitle: (
													<Space direction="vertical">
														<Typography.Text>
															Setiap listing yang dipilih akan menghasilkan 1 file PDF
															terpisah, untuk menggabungkan beberapa listing ke dalam 1
															PDF gunakan fitur{" "}
															<a
																href={
																	window.location.protocol +
																	"//" +
																	window.location.host +
																	"/my-list"
																}
																target="_blank"
																rel="noopener noreferrer"
																style={{
																	fontWeight: "bold",
																	textDecoration: "underline",
																}}
															>
																MyList
															</a>
														</Typography.Text>
													</Space>
												),
												okText: "Mask",
												cancelText: "Cancel",
												onOk: () => doDownloadMultiplePDF(key as "en" | "id"),
											});
										}
									},
								}}
							>
								<Button
									block
									type="primary"
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
										minWidth: 270,
									}}
									onClick={() => {
										if (selectedListings.length < 1) {
											return message.error("Please select a listing first before multiple mask!");
										}
									}}
								>
									<VerticalAlignBottomOutlined />
									<span>Mask Selected Listing ({selectedListings.length})</span>

									<DownOutlined />
								</Button>
							</Dropdown>
						</Col>
					)}
				</Filter>

				<Card
					bordered={false}
					size="small"
				>
					<Table
						loading={
							isLoading ||
							isDownloadLoading ||
							isDownloadLoadingMultiple ||
							isLoadingShare ||
							isLoadingMaskingListing
						}
						rowSelection={
							{
								type: "checkbox",
								...rowSelection,
							} as any
						}
						columns={COLUMNS}
						dataSource={data}
						rowKey="listingId"
						pagination={false}
						scroll={{ x: "100%" }}
					/>
					<CustomPagination
						current={pagination.page}
						total={pagination.totalData}
						defaultPageSize={pagination.perPage}
						pageSize={pagination.perPage}
						onChange={changePage}
					/>
				</Card>
			</Space>
		</React.Fragment>
	);
};
export default MaskingTable;
